import { Button, Tabs } from 'antd';
import { FileX, ShieldAlert } from 'lucide-react';
import type { ReactElement } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router';
import { DetailPageSkeleton } from '../../../shared/components/DetailPageSkeleton';
import { EmptyState } from '../../../shared/components/EmptyState';
import { PageHeader } from '../../../shared/components/PageHeader';
import { withinSection } from '../../../shared/components/section-trail';
import { TripActions } from '../components/TripActions';
import { TripHeader } from '../components/TripHeader';
import { TripTabBody } from '../components/TripTabBody';
import { useTripPermissions } from '../hooks/use-trip-permissions';
import { useTripPage, type TripPageState } from '../hooks/use-trip-page';
import { availableTripActions } from '../policies/trip-actions.policy';
import { visibleTripTabs } from '../policies/trip-tabs.policy';
import { TRIP_TABS, TRIP_TAB_LABELS, tripPaths, type TripTab } from '../trips.paths';

const TRIP_TRAIL = withinSection('dispatch');

function loadStateFallback(state: TripPageState, reload: () => void): ReactElement | undefined {
  switch (state.status) {
    case 'forbidden':
      return <Navigate to="/" replace />;
    case 'loading':
      return <DetailPageSkeleton label="Loading trip" breadcrumbs={TRIP_TRAIL} tabs={TRIP_TABS.length} />;
    case 'not-found':
      return <EmptyState icon={FileX} title="Trip not found" description="This trip doesn't exist, or the link is out of date." />;
    case 'error':
      return (
        <EmptyState icon={ShieldAlert} title="Couldn't load this trip" description="Something went wrong loading this trip.">
          <Button onClick={reload}>Try again</Button>
        </EmptyState>
      );
    case 'loaded':
      return undefined;
  }
}

/** The office's page for one trip: the facts and deadlines on top, the map and the working tabs below. */
export function TripPage({ tab }: { tab: TripTab }) {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { state, reload, readCount } = useTripPage(id);
  const permissions = useTripPermissions();

  const fallback = loadStateFallback(state, reload);
  if (fallback) return fallback;
  if (state.status !== 'loaded') return null;

  const { trip, eta, stops } = state;
  const hasDriver = trip.driver !== null;
  const canCapture = availableTripActions({ status: trip.status, hasDriver, ...permissions }).includes('capture');
  const tabs = visibleTripTabs({ hasDriver, canCapture, ...permissions });
  if (!tabs.includes(tab)) return <Navigate to={tripPaths.trip(trip.id)} replace />;

  return (
    <>
      <PageHeader breadcrumbs={TRIP_TRAIL} title={trip.number} actions={<TripActions trip={trip} onChanged={reload} />} />
      <div className="flex flex-col gap-4">
        <TripHeader trip={trip} eta={eta} stops={stops} />
        <Tabs
          activeKey={tab}
          onChange={(key) => navigate(tripPaths.tripTab(trip.id, key as TripTab))}
          items={tabs.map((key) => ({ key, label: TRIP_TAB_LABELS[key] }))}
        />
        <TripTabBody tab={tab} trip={trip} stops={stops} readCount={readCount} onChanged={reload} />
      </div>
    </>
  );
}
